import { useState, useEffect, useRef, useCallback } from 'react';
import { settings } from '../services/settingsService';
import { voiceTriggerService } from '../services/voiceTriggerService';

// Exponential backoff delays for retries (milliseconds)
// Android uses longer delays to avoid beeping from rapid mic acquire/release
const BACKOFF_DELAYS_DESKTOP = [100, 200, 400, 800, 1600];
const BACKOFF_DELAYS_ANDROID = [500, 1000, 2000, 3000, 5000];

// Fatal errors that should disable the trigger
const FATAL_ERRORS = ['not-allowed', 'network', 'service-not-allowed'];

// Android mic conflict errors - these indicate the stream interceptor couldn't share the mic
const ANDROID_MIC_CONFLICT_ERRORS = ['audio-capture', 'aborted'];

/**
 * Check if a phrase matches in the transcript.
 * Handles multi-word phrases by checking if all words appear in order.
 * @param {string} transcript - The recognized speech
 * @param {string} phrase - The phrase to match
 * @returns {boolean}
 */
const phraseMatches = (transcript, phrase) => {
  // Normalize both strings
  const normalizedTranscript = transcript.toLowerCase().trim();
  const normalizedPhrase = phrase.toLowerCase().trim();

  // Direct substring match (handles single words and exact phrases)
  if (normalizedTranscript.includes(normalizedPhrase)) {
    return true;
  }

  // For multi-word phrases, check if all words appear in the transcript
  const phraseWords = normalizedPhrase.split(/\s+/);
  if (phraseWords.length > 1) {
    // Check if all phrase words appear in the transcript (in any order for flexibility)
    const allWordsPresent = phraseWords.every(word => normalizedTranscript.includes(word));
    if (allWordsPresent) {
      return true;
    }
  }

  return false;
};

/**
 * Hook for voice-activated stop trigger during recording.
 * Redesigned to accept an external stream and prevent UI flickering.
 *
 * @param {Object} options
 * @param {boolean} options.enabled - Whether the hook should be active
 * @param {MediaStream} [options.stream] - Optional external stream from audioStreamService
 * @param {function} options.onStopDetected - Callback when stop word detected, receives timestamp
 * @param {function} [options.onFatalError] - Optional callback for unrecoverable errors
 * @param {Object} [options.voiceStopInfoRef] - Optional ref to write stop info synchronously (for mobile timing)
 * @param {function} [options.onDebug] - Optional callback for debug info (for mobile debugging)
 *
 * @returns {{ isListening: boolean, lastHeardText: string, retryCount: number }}
 */
export const useVoiceStopTrigger = ({
  enabled,
  stream,
  onStopDetected,
  onFatalError,
  voiceStopInfoRef,
  onDebug
}) => {
  // State for UI (updated sparingly to prevent flickering)
  const [isListening, setIsListening] = useState(false);
  const [displayText, setDisplayText] = useState('');

  // Refs for high-frequency updates (no re-renders)
  const lastHeardTextRef = useRef('');
  const recognitionRef = useRef(null);
  const hasTriggeredRef = useRef(false);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef(null);
  const isStoppingRef = useRef(false);

  // Store callbacks in refs to avoid dependency cycle
  const onStopDetectedRef = useRef(onStopDetected);
  const onFatalErrorRef = useRef(onFatalError);
  useEffect(() => {
    onStopDetectedRef.current = onStopDetected;
    onFatalErrorRef.current = onFatalError;
  }, [onStopDetected, onFatalError]);

  // Expose retryCount as state for UI (updated less frequently)
  const [retryCount, setRetryCount] = useState(0);

  const stopRecognition = useCallback(() => {
    isStoppingRef.current = true;

    // Clear any pending retry
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore - may already be stopped
      }
      // Clear handlers to allow GC of the SpeechRecognition instance
      // Without this, event handlers keep references and prevent garbage collection
      recognitionRef.current.onstart = null;
      recognitionRef.current.onresult = null;
      recognitionRef.current.onerror = null;
      recognitionRef.current.onend = null;
      recognitionRef.current = null;
    }

    setIsListening(false);
  }, []);

  const startRecognition = useCallback(() => {
    // Don't start if we're stopping or already triggered
    if (isStoppingRef.current || hasTriggeredRef.current) {
      return;
    }

    // On Android, voice stop trigger cannot work during recording because:
    // - Recording needs exclusive mic access
    // - Speech recognition also needs mic access
    // - Android doesn't allow simultaneous mic access
    // - Stream cloning/interceptor approach doesn't work with Web Speech API
    // Users must use the stop button on Android.
    const isAndroid = /android/i.test(navigator.userAgent);
    if (isAndroid) {
      console.log('[VoiceStopTrigger] Android detected - voice stop disabled during recording (mic conflict)');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.log('Web Speech API not supported');
      onFatalErrorRef.current?.();
      return;
    }

    // Clear any pending retry timeout first
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    // Stop any existing recognition first and clear handlers for GC
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore
      }
      recognitionRef.current.onstart = null;
      recognitionRef.current.onresult = null;
      recognitionRef.current.onerror = null;
      recognitionRef.current.onend = null;
      recognitionRef.current = null;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = voiceTriggerService.getLanguage();
    recognition.maxAlternatives = 3;

    recognition.onstart = () => {
      console.log('[VoiceStopTrigger] onstart - isStopping:', isStoppingRef.current);
      // Only update state once when we actually start listening
      if (!isStoppingRef.current) {
        setIsListening(true);
        hasTriggeredRef.current = false;
      }
    };

    recognition.onresult = (event) => {
      console.log('[VoiceStopTrigger] onresult - hasTriggered:', hasTriggeredRef.current, 'isStopping:', isStoppingRef.current);
      if (hasTriggeredRef.current || isStoppingRef.current) {
        console.log('[VoiceStopTrigger] onresult early return');
        return;
      }

      const lastResult = event.results[event.results.length - 1];
      const transcript = lastResult[0].transcript.toLowerCase().trim();
      console.log('[VoiceStopTrigger] heard:', transcript, 'isFinal:', lastResult.isFinal);

      // Store in ref (no re-render)
      lastHeardTextRef.current = transcript;

      // Check for stop keywords using phrase matching - find which keyword matched
      const stopKeywords = settings.stopTrigger.getKeywords();
      const matchedKeyword = stopKeywords.find(keyword => phraseMatches(transcript, keyword));

      // Debug log ALL transcripts (not just matches) for troubleshooting
      const confidence = lastResult[0].confidence;
      if (onDebug) {
        onDebug({
          type: 'transcript',
          transcript,
          confidence: `${(confidence * 100).toFixed(1)}%`,
          isFinal: lastResult.isFinal,
          matchedKeyword: matchedKeyword || null,
          keywords: stopKeywords.join(', ')
        });
      }

      // Only update display text on final results (prevents flickering)
      if (lastResult.isFinal) {
        setDisplayText(transcript);
      }

      if (matchedKeyword && !hasTriggeredRef.current) {
        console.log(`Stop trigger phrase detected: "${matchedKeyword}" (in transcript: "${transcript}")`);
        hasTriggeredRef.current = true;

        // Capture timestamp and matched phrase for accurate audio trimming
        const detectionTimestamp = Date.now();

        // CRITICAL: Write to external ref SYNCHRONOUSLY before any async operations
        // This ensures the trim info is available on mobile (iOS Safari) where the
        // MediaRecorder's onstop fires BEFORE the callback completes
        console.log('[VoiceStopTrigger] voiceStopInfoRef exists:', !!voiceStopInfoRef);
        if (voiceStopInfoRef) {
          voiceStopInfoRef.current = {
            detectionTimestamp,
            detectedPhrase: matchedKeyword
          };
          console.log('[VoiceStopTrigger] Wrote to voiceStopInfoRef:', JSON.stringify(voiceStopInfoRef.current));
          // Call debug callback if provided (for mobile on-screen debugging)
          if (onDebug) {
            onDebug({ hookWroteRef: true, hookWriteTime: detectionTimestamp, hookData: { detectionTimestamp, detectedPhrase: matchedKeyword } });
          }
        } else {
          console.log('[VoiceStopTrigger] WARNING: voiceStopInfoRef is null/undefined!');
          if (onDebug) {
            onDebug({ hookWroteRef: false, hookWriteTime: detectionTimestamp, warning: 'voiceStopInfoRef is null' });
          }
        }

        // Stop recognition before calling callback
        stopRecognition();

        // Call the callback with timestamp and matched phrase (use ref to avoid stale closure)
        onStopDetectedRef.current?.(detectionTimestamp, matchedKeyword);
      }
    };

    recognition.onerror = (event) => {
      console.log('Speech recognition error:', event.error);
      const isAndroid = /android/i.test(navigator.userAgent);

      // Check if this is a fatal error
      if (FATAL_ERRORS.includes(event.error)) {
        console.error('Fatal speech recognition error:', event.error);
        stopRecognition();
        onFatalErrorRef.current?.();
        return;
      }

      // On Android, check for mic conflict errors (stream interceptor failed)
      // These indicate the mic can't be shared, so we gracefully disable voice stop
      if (isAndroid && ANDROID_MIC_CONFLICT_ERRORS.includes(event.error)) {
        // Only treat as fatal if we've had multiple consecutive failures
        if (retryCountRef.current >= 3) {
          console.warn('[VoiceStopTrigger] Android mic conflict - disabling voice stop after 3 retries');
          stopRecognition();
          // Don't call onFatalError - this is expected on some Android devices
          // User can still use the stop button
          return;
        }
      }

      // Transient error - retry with exponential backoff
      // Note: onend will also fire after onerror, but we only restart from here
      if ((event.error === 'no-speech' || event.error === 'aborted') &&
          enabled && !hasTriggeredRef.current && !isStoppingRef.current) {
        // Use longer delays on Android to avoid beeping
        const backoffDelays = isAndroid ? BACKOFF_DELAYS_ANDROID : BACKOFF_DELAYS_DESKTOP;
        const delay = backoffDelays[Math.min(retryCountRef.current, backoffDelays.length - 1)];
        retryCountRef.current++;

        // Update state less frequently (only every few retries)
        if (retryCountRef.current % 3 === 0 || retryCountRef.current <= 1) {
          setRetryCount(retryCountRef.current);
        }

        // Clear any existing timeout before setting new one
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }
        retryTimeoutRef.current = setTimeout(() => {
          if (enabled && !hasTriggeredRef.current && !isStoppingRef.current) {
            startRecognition();
          }
        }, delay);
      }
    };

    recognition.onend = () => {
      console.log('[VoiceStopTrigger] onend - hasTriggered:', hasTriggeredRef.current, 'isStopping:', isStoppingRef.current, 'enabled:', enabled, 'retryTimeout:', !!retryTimeoutRef.current);
      // Don't restart from onend - only onerror handles restarts to avoid race conditions
      // Reset backoff counter on clean end (no error)
      if (!hasTriggeredRef.current && !isStoppingRef.current) {
        // If there's no pending retry from onerror, schedule one
        // This handles the case where recognition ends normally (timeout)
        if (!retryTimeoutRef.current && enabled) {
          retryCountRef.current = 0;
          setRetryCount(0);
          // Use longer delay on Android to avoid beeping
          const isAndroid = /android/i.test(navigator.userAgent);
          const delay = isAndroid ? 500 : 100;
          retryTimeoutRef.current = setTimeout(() => {
            if (enabled && !hasTriggeredRef.current && !isStoppingRef.current) {
              startRecognition();
            }
          }, delay);
          return;
        }
      }

      // Only set isListening to false if we're truly stopping (not restarting)
      if (!retryTimeoutRef.current) {
        setIsListening(false);
      }
    };

    recognitionRef.current = recognition;

    try {
      recognition.start();
    } catch (e) {
      console.error('Error starting speech recognition:', e);
      // Try again with backoff - use longer delays on Android
      const isAndroid = /android/i.test(navigator.userAgent);
      const backoffDelays = isAndroid ? BACKOFF_DELAYS_ANDROID : BACKOFF_DELAYS_DESKTOP;
      const delay = backoffDelays[Math.min(retryCountRef.current, backoffDelays.length - 1)];
      retryCountRef.current++;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      retryTimeoutRef.current = setTimeout(() => {
        if (enabled && !hasTriggeredRef.current && !isStoppingRef.current) {
          startRecognition();
        }
      }, delay);
    }
  }, [enabled, stopRecognition]); // Removed callback deps - using refs instead

  useEffect(() => {
    if (enabled) {
      // Reset state for new session
      hasTriggeredRef.current = false;
      isStoppingRef.current = false;
      retryCountRef.current = 0;
      setRetryCount(0);
      setDisplayText('');

      startRecognition();
    } else {
      stopRecognition();
    }

    return () => {
      stopRecognition();
    };
  }, [enabled, startRecognition, stopRecognition]);

  return {
    isListening,
    lastHeardText: displayText,
    retryCount,
  };
};
